import { useCallback, useEffect, useRef, useState } from 'react';
import { isNearBottom, nextFollowState } from '../utils/scrollFollow';

const DETACH_KEYS = new Set(['PageUp', 'Home', 'ArrowUp']);

/**
 * Pin a scroll container to the bottom while the user wants to follow,
 * and detach immediately on upward wheel/touch/keyboard intent.
 *
 * @param {Array<{ type?: string }>} messages
 * @returns {{ containerRef: React.RefObject, showJumpButton: boolean, jumpToLatest: function }}
 */
export function useFollowScroll(messages) {
  const containerRef = useRef(null);
  const followingRef = useRef(true);
  const lastProgrammaticTopRef = useRef(null);
  const lastTouchYRef = useRef(null);
  const prevLengthRef = useRef(messages.length);
  const pinningRef = useRef(false);
  const rafRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const [showJump, setShowJump] = useState(false);

  const syncJumpButton = useCallback(() => {
    const next = !followingRef.current && messagesRef.current.length > 0;
    setShowJump((prev) => (prev === next ? prev : next));
  }, []);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = containerRef.current;
    if (!el) return;

    const write = () => {
      rafRef.current = null;
      const node = containerRef.current;
      if (!node) return;
      if (smooth) {
        node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
        lastProgrammaticTopRef.current = node.scrollHeight - node.clientHeight;
      } else {
        if (!followingRef.current) return;
        node.scrollTop = node.scrollHeight;
        lastProgrammaticTopRef.current = node.scrollTop;
      }
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (smooth) {
      write();
    } else {
      rafRef.current = requestAnimationFrame(write);
    }
  }, []);

  const jumpToLatest = useCallback(() => {
    followingRef.current = true;
    pinningRef.current = true;
    setShowJump(false);
    scrollToBottom(true);
  }, [scrollToBottom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const detach = () => {
      if (!followingRef.current) return;
      followingRef.current = false;
      pinningRef.current = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      syncJumpButton();
    };

    const onWheel = (event) => {
      if (event.deltaY < 0) detach();
    };

    const onTouchStart = (event) => {
      lastTouchYRef.current = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event) => {
      const y = event.touches[0]?.clientY;
      if (y == null || lastTouchYRef.current == null) return;
      if (y > lastTouchYRef.current) detach();
      lastTouchYRef.current = y;
    };

    const onKeyDown = (event) => {
      if (DETACH_KEYS.has(event.key)) detach();
    };

    const onScroll = () => {
      if (el.clientHeight === 0 || el.scrollHeight === 0) return;

      const metrics = {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      };

      if (pinningRef.current) {
        if (isNearBottom(metrics)) pinningRef.current = false;
        return;
      }

      const next = nextFollowState({
        following: followingRef.current,
        metrics,
        lastProgrammaticTop: lastProgrammaticTopRef.current,
      });
      if (next !== followingRef.current) {
        followingRef.current = next;
        syncJumpButton();
      }
    };

    const onResize = () => {
      if (followingRef.current) scrollToBottom(false);
    };

    const opts = { passive: true };
    el.addEventListener('wheel', onWheel, opts);
    el.addEventListener('touchstart', onTouchStart, opts);
    el.addEventListener('touchmove', onTouchMove, opts);
    el.addEventListener('keydown', onKeyDown, opts);
    el.addEventListener('scroll', onScroll, opts);

    const observer = new ResizeObserver(onResize);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [messages.length, scrollToBottom, syncJumpButton]);

  useEffect(() => {
    const prevLength = prevLengthRef.current;
    const length = messages.length;

    if (length === 0) {
      followingRef.current = true;
      pinningRef.current = false;
      setShowJump(false);
      prevLengthRef.current = 0;
      return;
    }

    const grew = length > prevLength;
    const grewFromEmpty = prevLength === 0 && grew;
    const lastIsUser = messages[length - 1]?.type === 'user';

    if (grewFromEmpty || (grew && lastIsUser)) {
      followingRef.current = true;
      setShowJump(false);
      scrollToBottom(false);
    } else if (followingRef.current) {
      scrollToBottom(false);
    }

    prevLengthRef.current = length;
  }, [messages, scrollToBottom]);

  return { containerRef, showJumpButton: showJump, jumpToLatest };
}
